import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import {
  LayoutDashboard, ShoppingCart, CalendarClock, Dumbbell, Apple, Users, LogOut, Menu, X, Sun, Moon, History, Contact, UserCheck, BarChart3, KeyRound, BookOpen, ScrollText,
} from "lucide-react";
import Logo from "@/components/Logo";
import ChangePassword from "@/components/ChangePassword";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/caisse", label: "Caisse", icon: ShoppingCart, testid: "nav-caisse" },
  { to: "/historique", label: "Historique caisses", icon: History, testid: "nav-historique" },
  { to: "/membres", label: "Membres", icon: Contact, testid: "nav-membres" },
  { to: "/presences", label: "Présences", icon: UserCheck, perm: "presences", testid: "nav-presences" },
  { to: "/rapports", label: "Rapport mensuel", icon: BarChart3, perm: "rapports", testid: "nav-rapports" },
  { to: "/seances", label: "Séances", icon: CalendarClock, testid: "nav-seances" },
  { to: "/equipements", label: "Équipements", icon: Dumbbell, testid: "nav-equipements" },
  { to: "/nutrition", label: "Nutrition", icon: Apple, testid: "nav-nutrition" },
  { to: "/utilisateurs", label: "Utilisateurs", icon: Users, admin: true, testid: "nav-utilisateurs" },
  { to: "/journal", label: "Journal d'activité", icon: ScrollText, admin: true, testid: "nav-journal" },
];

export default function Layout() {
  const { user, logout, can } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);

  const links = NAV.filter((l) => (!l.admin || user?.role === "admin") && (!l.perm || can(l.perm)));

  const sidebar = (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-5 py-6 border-b border-border">
        <Logo />
        <div>
          <div className="font-display font-bold text-lg leading-tight tracking-wide text-foreground">LAC ROSE</div>
          <div className="text-[11px] uppercase tracking-widest text-emerald-600 dark:text-emerald-400 font-semibold">Fitness</div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1" data-testid="sidebar-nav">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            data-testid={l.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                isActive
                  ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted border border-transparent"
              }`
            }
          >
            <l.icon className="w-4 h-4" />
            {l.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-4 border-t border-border space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-muted border border-border flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold text-sm">
            {user?.nom?.charAt(0)?.toUpperCase() || "?"}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground truncate" data-testid="sidebar-user-name">{user?.nom}</div>
            <Badge variant="outline" className="text-[10px] border-emerald-500/40 text-emerald-600 dark:text-emerald-400" data-testid="sidebar-user-role">
              {user?.role === "admin" ? "Administrateur" : "Employé"}
            </Badge>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          data-testid="theme-toggle-button"
          className="w-full border-border text-foreground/80 hover:bg-muted"
          onClick={toggle}
        >
          {theme === "dark" ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
          {theme === "dark" ? "Mode clair" : "Mode sombre"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          data-testid="change-password-button"
          className="w-full border-border text-foreground/80 hover:bg-muted"
          onClick={() => setPwdOpen(true)}
        >
          <KeyRound className="w-4 h-4 mr-2" /> Mon mot de passe
        </Button>
        <Button
          variant="outline"
          size="sm"
          data-testid="logout-button"
          className="w-full border-border text-foreground/80 hover:bg-muted hover:text-red-600 dark:hover:text-red-400"
          onClick={async () => { await logout(); navigate("/login"); }}
        >
          <LogOut className="w-4 h-4 mr-2" /> Déconnexion
        </Button>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/manuel`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="manuel-link"
          className="flex items-center justify-center gap-2 w-full h-9 rounded-md border border-border text-sm text-foreground/80 hover:bg-muted transition-colors"
        >
          <BookOpen className="w-4 h-4" /> Manuel d'utilisation
        </a>
        <p className="text-[10px] leading-relaxed text-muted-foreground text-center" data-testid="sidebar-branding">
          Ce logiciel est créé par IBM<br />© Copyright Ibn Bachir MultiMedia<br />Tél : 77 571 93 98
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background text-foreground" data-theme={theme}>
      <ChangePassword open={pwdOpen} onClose={() => setPwdOpen(false)} />
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 bg-card border-r border-border z-40">
        {sidebar}
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-card border-r border-border">
            {sidebar}
          </aside>
        </div>
      )}

      <div className="lg:pl-64 flex flex-col min-h-screen">
        <header className="lg:hidden sticky top-0 z-30 bg-card/95 backdrop-blur border-b border-border px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" size="icon" data-testid="mobile-menu-button" onClick={() => setOpen(!open)}>
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
          <div className="font-display font-bold tracking-wide">LAC ROSE FITNESS</div>
          <div className="w-9" />
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
        <footer className="border-t border-border px-4 py-3 text-center text-xs text-muted-foreground" data-testid="app-footer">
          Ce logiciel est créé par IBM — © Copyright Ibn Bachir MultiMedia — Tél : 77 571 93 98
        </footer>
      </div>
    </div>
  );
}
